/**
 * An algorithm for computing the convex hull on a plane.
 * @module
 */
import type { PlaneBSP } from "./bspTypes";
import { polyLine2ConvexHullVertexIndices } from "./polyLine2";
import { polyLine2New } from "./polyLine2Types";

function convexHullAxesFromPlaneEquation({ nX, nY, nZ }: PlaneBSP): { hAxis: number; vAxis: number; isCCW: boolean } {
  let hAxis: number = 0; // by default convex hull horizontal axis is x-axis
  let vAxis: number = 1; // by default convex hull vertical axis is y-axis
  let isCCW = nZ > 0.0; // if false need to reverse vertex winding order

  const x = Math.abs(nX);
  const y = Math.abs(nY);
  const z = Math.abs(nZ);

  if (x > y) {
    if (x > z) {
      // -ve or +ve x axis is dominant in plane normal not z
      hAxis = 1; // y-axis
      vAxis = 2; // z-axis
      isCCW = nX > 0.0;
    }
  } else {
    if (y > z) {
      // -ve or +ve y axis is dominant in plane normal not z
      hAxis = 2; // z-axis
      vAxis = 0; // x-axis
      isCCW = nY > 0.0;
    }
  }

  return { hAxis, vAxis, isCCW };
}

export function convexHullOnPlane(planeEqn: PlaneBSP, coplanarVertices: number[] | null): number[] {
  const convexHullVertices: number[] = [];

  if (coplanarVertices && coplanarVertices.length >= 9) {
    const { hAxis, vAxis, isCCW } = convexHullAxesFromPlaneEquation(planeEqn); // turn 3d problem into a 2d one
    const polyLine = polyLine2New((coplanarVertices.length / 3) * 2);
    for (let i = 0, j = 0; i < coplanarVertices.length; i += 3) {
      polyLine[j++] = coplanarVertices[i + hAxis];
      polyLine[j++] = coplanarVertices[i + vAxis];
    }

    let convexHullVertexIndices = polyLine2ConvexHullVertexIndices(polyLine);
    if (convexHullVertexIndices.length > 2) {
      if (isCCW) {
        // need to reverse winding order of convex hull to be CCW with respect to +ve dominant axis in plane normal
        const slicedVertexIndex = convexHullVertexIndices[0];
        convexHullVertexIndices = convexHullVertexIndices.slice(1).reverse();
        convexHullVertexIndices.unshift(slicedVertexIndex);
      }
      for (const vertexIndex of convexHullVertexIndices) {
        const i = vertexIndex * 3;
        const x = coplanarVertices[i];
        const y = coplanarVertices[i + 1];
        const z = coplanarVertices[i + 2];
        convexHullVertices.push(x, y, z);
      }
    }
  }

  return convexHullVertices;
}
